import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MessageSquare, Search, RefreshCw, ChevronLeft, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * SabrilAdminPage — Internal CRM dashboard at /admin/sabril
 * Lists every conversation that happened with the Sabril Closer bot
 * (across all 4 pages: home, offre, prsnl, app) and lets you read the
 * full transcript + see the captured lead (if any).
 *
 * NOTE: This is an internal-use page — knowing the URL is enough for now
 * (security is via obscurity + no public links). Tu peux ajouter un auth
 * gate plus tard si besoin.
 */

const PAGE_LABEL = {
  prsnl: 'PRSNL · Coaching',
  offre: 'Ebook 147€',
  app: 'App SaaS',
  home: 'Home',
};
const PAGE_COLOR = {
  prsnl: '#F05A1A',
  offre: '#22C55E',
  app: '#7B61FF',
  home: '#FFB341',
};

const fmtDateTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch (_) { return iso; }
};

const SabrilAdminPage = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'with_lead' | 'no_lead'
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null); // { session_id, messages, lead }
  const [selectedLoading, setSelectedLoading] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sabril/conversations?limit=200`);
      const data = await res.json();
      setConversations(data.conversations || []);
    } catch (e) {
      console.error('Load conv list failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const openConversation = async (session_id) => {
    setSelectedLoading(true);
    setSelected({ session_id, messages: [], lead: null });
    try {
      const res = await fetch(`${API}/api/sabril/conversation/${encodeURIComponent(session_id)}`);
      const data = await res.json();
      setSelected({ session_id, messages: data.messages || [], lead: data.lead });
    } catch (e) {
      console.error('Load conv detail failed', e);
    } finally {
      setSelectedLoading(false);
    }
  };

  const filtered = conversations.filter((c) => {
    if (filter === 'with_lead' && !c.has_lead) return false;
    if (filter === 'no_lead' && c.has_lead) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${c.session_id} ${c.last_message} ${c.lead_name || ''} ${c.lead_phone || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-[#0F0F12] text-white pb-20">
      <header className="sticky top-0 z-20 bg-[#0F0F12]/90 backdrop-blur border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <h1 className="font-bebas text-2xl sm:text-3xl tracking-tight flex-1">
            <span className="text-[#F05A1A]">SABRIL CLOSER</span> · CRM
          </h1>
          <button
            type="button"
            onClick={() => {
              const API_URL = process.env.REACT_APP_BACKEND_URL;
              let token = localStorage.getItem('admin_ebook_token');
              if (!token) {
                token = window.prompt('Token admin ebook (fourni par Sabril) :');
                if (!token) return;
                localStorage.setItem('admin_ebook_token', token.trim());
              }
              window.open(`${API_URL}/api/ebook/download?token=${encodeURIComponent(token.trim())}`, '_blank');
            }}
            className="hidden sm:flex items-center gap-2 px-4 h-9 rounded-full bg-gradient-to-r from-[#C9A87A] to-[#8B6520] text-black font-semibold text-xs tracking-wide hover:brightness-110 transition-all shadow-md"
            data-testid="admin-download-ebook-btn"
            title="Télécharger le PDF Ebook (admin)"
          >
            <Download className="w-3.5 h-3.5" strokeWidth={2.5} />
            EBOOK PDF
          </button>
          <button
            type="button"
            onClick={() => {
              const API_URL = process.env.REACT_APP_BACKEND_URL;
              let token = localStorage.getItem('admin_ebook_token');
              if (!token) {
                token = window.prompt('Token admin ebook :');
                if (!token) return;
                localStorage.setItem('admin_ebook_token', token.trim());
              }
              window.open(`${API_URL}/api/ebook/download?token=${encodeURIComponent(token.trim())}`, '_blank');
            }}
            className="sm:hidden w-9 h-9 rounded-full bg-gradient-to-r from-[#C9A87A] to-[#8B6520] text-black flex items-center justify-center shadow-md"
            data-testid="admin-download-ebook-btn-mobile"
            aria-label="Télécharger PDF"
          >
            <Download className="w-4 h-4" strokeWidth={2.5} />
          </button>
          <button
            onClick={loadList}
            className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors"
            data-testid="sabril-admin-refresh"
            aria-label="Rafraîchir"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} strokeWidth={2.2} />
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-5">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" strokeWidth={2} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher (prénom, tél, message…)"
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder-white/40 focus:outline-none focus:border-[#F05A1A]/50"
              data-testid="sabril-admin-search"
            />
          </div>
          <div className="flex gap-1.5">
            {[
              { v: 'all', label: 'Toutes' },
              { v: 'with_lead', label: '🔥 Leads capturés' },
              { v: 'no_lead', label: 'Sans lead' },
            ].map((f) => (
              <button
                key={f.v}
                onClick={() => setFilter(f.v)}
                className={`px-3 py-2 rounded-xl text-[12px] font-bold whitespace-nowrap transition-all ${
                  filter === f.v
                    ? 'bg-[#F05A1A] text-white'
                    : 'bg-white/5 border border-white/10 text-white/65 hover:bg-white/10'
                }`}
                data-testid={`sabril-admin-filter-${f.v}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Two-column layout on desktop, single column on mobile */}
        <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-4">
          {/* LEFT: list */}
          <div className="space-y-2">
            {loading && (
              <div className="text-center text-white/40 text-sm py-6">Chargement…</div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="text-center text-white/40 text-sm py-10 border border-dashed border-white/10 rounded-xl">
                Aucune conversation pour ce filtre.
              </div>
            )}
            {filtered.map((c) => {
              const color = PAGE_COLOR[c.page] || '#999';
              const label = PAGE_LABEL[c.page] || c.page;
              const isActive = selected?.session_id === c.session_id;
              return (
                <button
                  key={c.session_id}
                  onClick={() => openConversation(c.session_id)}
                  className={`block w-full text-left rounded-2xl p-3.5 transition-all border ${
                    isActive
                      ? 'bg-white/[0.08] border-[#F05A1A]/40'
                      : 'bg-white/[0.025] border-white/[0.08] hover:bg-white/[0.05]'
                  }`}
                  data-testid={`sabril-conv-item-${c.session_id}`}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <span
                      className="text-[9px] font-bold uppercase tracking-[2px] px-2 py-0.5 rounded-full"
                      style={{ color, background: `${color}1a`, border: `1px solid ${color}33` }}
                    >
                      {label}
                    </span>
                    {c.has_lead && (
                      <span className="text-[9px] font-black uppercase tracking-[2px] px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 border border-green-500/30">
                        🔥 Lead
                      </span>
                    )}
                    <span className="ml-auto text-[10px] text-white/40">
                      {fmtDateTime(c.last_seen)}
                    </span>
                  </div>
                  <p className="font-bold text-sm text-white truncate">
                    {c.lead_name || `Session ${c.session_id.slice(-8)}`}
                  </p>
                  <p className="text-[12px] text-white/55 line-clamp-2 mt-0.5 leading-snug">
                    {c.last_role === 'user' ? '👤 ' : '🟠 '}{c.last_message || '—'}
                  </p>
                  <p className="text-[10px] text-white/35 mt-1.5">
                    {c.message_count} message{c.message_count > 1 ? 's' : ''}
                  </p>
                </button>
              );
            })}
          </div>

          {/* RIGHT: transcript */}
          <div className="hidden lg:block">
            {!selected ? (
              <div className="h-full min-h-[500px] flex items-center justify-center rounded-2xl border border-dashed border-white/10 text-white/40 text-sm">
                Sélectionne une conversation pour voir le transcript.
              </div>
            ) : (
              <ConversationDetail loading={selectedLoading} data={selected} />
            )}
          </div>
        </div>

        {/* Mobile detail overlay */}
        {selected && (
          <div className="lg:hidden fixed inset-0 z-30 bg-[#0F0F12] overflow-y-auto" data-testid="sabril-conv-detail-mobile">
            <div className="sticky top-0 bg-[#0F0F12]/95 backdrop-blur border-b border-white/10 px-3 py-3 flex items-center gap-2">
              <button
                onClick={() => setSelected(null)}
                className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"
                data-testid="sabril-conv-back"
              >
                <ChevronLeft className="w-4 h-4" strokeWidth={2.4} />
              </button>
              <p className="font-bebas text-xl tracking-tight">Conversation</p>
            </div>
            <div className="p-3">
              <ConversationDetail loading={selectedLoading} data={selected} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ConversationDetail = ({ loading, data }) => {
  if (loading || !data) return <div className="text-white/40 text-sm py-6 text-center">Chargement…</div>;
  const { messages = [], lead } = data;
  return (
    <div className="space-y-3">
      {/* Lead summary card on top */}
      {lead ? (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border-2 border-green-500/30 bg-green-500/[0.07] p-4"
          data-testid="sabril-conv-lead-card"
        >
          <p className="text-[10px] font-black uppercase tracking-[3px] text-green-400 mb-2">
            🔥 Lead qualifié
          </p>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <p className="text-[10px] text-white/45 uppercase tracking-[2px]">Prénom</p>
              <p className="font-bold text-white">{lead.first_name || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] text-white/45 uppercase tracking-[2px]">Créneau</p>
              <p className="font-bold text-white">{lead.best_slot || '—'}</p>
            </div>
            <div className="col-span-2">
              <p className="text-[10px] text-white/45 uppercase tracking-[2px]">Téléphone</p>
              <a href={`tel:${lead.phone}`} className="font-bold text-orange-400 underline">{lead.phone || '—'}</a>
            </div>
            <div className="col-span-2">
              <p className="text-[10px] text-white/45 uppercase tracking-[2px]">Offre</p>
              <p className="font-bold text-white">{(lead.recommended_offer || '—').toUpperCase()}</p>
            </div>
            {lead.summary && (
              <div className="col-span-2">
                <p className="text-[10px] text-white/45 uppercase tracking-[2px]">Résumé</p>
                <p className="text-white/85 text-[13px] leading-relaxed">{lead.summary}</p>
              </div>
            )}
          </div>
          <div className="mt-3 flex gap-2">
            <a
              href={`tel:${lead.phone}`}
              className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#F05A1A] text-white text-xs font-black px-3 py-2 rounded-lg"
              data-testid="sabril-conv-call-btn"
            >
              <Phone className="w-3.5 h-3.5" strokeWidth={2.4} /> Appeler
            </a>
          </div>
        </motion.div>
      ) : (
        <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-3 flex items-center gap-2 text-white/55 text-[12px]">
          <MessageSquare className="w-4 h-4" strokeWidth={2.2} />
          Conversation sans lead capturé.
        </div>
      )}

      {/* Transcript */}
      <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
        {messages.map((m) => (
          <div
            key={m.id}
            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap ${
                m.role === 'user'
                  ? 'bg-[#F05A1A] text-white rounded-br-sm'
                  : 'bg-white/[0.06] border border-white/[0.08] text-white/90 rounded-bl-sm'
              }`}
            >
              {m.content}
              <p className={`text-[9px] mt-1 ${m.role === 'user' ? 'text-white/70' : 'text-white/35'}`}>
                {fmtDateTime(m.created_at)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SabrilAdminPage;
